import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Download, Loader2, RefreshCw } from 'lucide-react';
import AppHeader from '../components/AppHeader';
import ReportMarkdown from '../components/ReportMarkdown';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';

export default function DetailedReport() {
  const { assessmentId } = useParams();
  const { user } = useAuth();
  const userId = user?.id;
  const nav = useNavigate();
  const [assessment, setAssessment] = useState(null);
  const [error, setError] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [activeTab, setActiveTab] = useState('detailed_report');
  const access = assessment?.payment;
  useEffect(() => {
    if (!userId) return;
    let active = true;
    setAssessment(null); setError('');
    api.get(`/assessments/${assessmentId}`).then(({ data }) => {
      if (active) {
        if (!data.payment || !data.reports?.detailed_report) nav(`/payment/${assessmentId}`, { replace: true });
        else setAssessment(data);
      }
    }).catch(() => { if (active) setError('The report could not be loaded. Please open it from History.'); });
    return () => { active = false; };
  }, [userId, assessmentId, nav]);
  const download = async () => {
    setDownloading(true); setError('');
    try {
      const { data } = await api.post('/reports/download', { assessment_id: Number(assessmentId) }, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([data]));
      const a = document.createElement('a'); a.href = url; a.download = 'AI_Readiness_Report.docx'; a.click(); URL.revokeObjectURL(url);
    } catch { setError('Download failed. Please try again.'); }
    finally { setDownloading(false); }
  };
  return <div className="min-h-screen bg-white"><AppHeader /><main className="max-w-5xl mx-auto px-6 py-10">
    <Link to="/history" className="text-sm text-zinc-600 hover:text-[#002FA7]">← Back to assessments</Link>
    {access?.mode === "simulated" && <div role="status" className="mt-6 border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">Payment simulated successfully. No money was charged.</div>}
    {error && <div role="alert" className="mt-4 text-red-700">{error}</div>}
    {!assessment && !error && <div role="status" className="mt-8 flex gap-2"><Loader2 className="w-5 h-5 animate-spin" /> Loading detailed report…</div>}
    {assessment && <>
      <div className="mt-8 flex flex-wrap justify-between gap-4 items-center"><div><div className="text-xs text-zinc-500">{assessment.form?.company_information?.['Company Name']}</div><h1 className="font-display text-3xl font-bold mt-2">Detailed Report</h1></div><button disabled={downloading} onClick={download} className="inline-flex gap-2 bg-[#002FA7] text-white px-5 py-3 text-sm disabled:opacity-50"><Download className="w-4 h-4" /> {downloading ? 'Downloading…' : 'Download .docx'}</button></div>
      <div className="mt-8 flex flex-wrap gap-2" role="tablist" aria-label="Report sections">
        {[
          ['business_overview', 'Business Overview'],
          ['ai_readiness', 'AI Readiness'],
          ['detailed_report', 'Detailed Analysis'],
        ].map(([key, label]) => <button key={key} role="tab" id={`detail-tab-${key}`} aria-selected={activeTab === key} aria-controls="detail-report-panel" onClick={() => setActiveTab(key)} className={`px-5 py-3 text-sm border ${activeTab === key ? 'bg-[#002FA7] border-[#002FA7] text-white' : 'border-zinc-200 text-zinc-600 hover:text-[#002FA7]'}`}>{label}</button>)}
      </div>
      <div id="detail-report-panel" role="tabpanel" aria-labelledby={`detail-tab-${activeTab}`} className="prose-startin border border-zinc-200 p-6 md:p-8 mt-4"><ReportMarkdown text={assessment.reports[activeTab]} /></div>
      <div className="mt-8 pt-6 border-t border-zinc-200 flex flex-wrap gap-4 items-center justify-between"><div><button disabled={access.regenerations_remaining <= 0} onClick={() => nav('/assessment', { state: { assessment, regenerate: true } })} className="inline-flex gap-2 items-center border border-zinc-300 px-5 py-3 text-sm disabled:opacity-50 disabled:cursor-not-allowed"><RefreshCw className="w-4 h-4" /> Regenerate</button><p className="text-xs text-zinc-500 mt-2">{access.regenerations_remaining} of 3 regenerations remaining</p></div><Link to={`/reports/${assessmentId}/next-steps`} className="bg-[#002FA7] text-white px-5 py-3 text-sm">Next Steps →</Link></div>
    </>}
  </main></div>;
}
